// src/utils/slugUtils.js

/**
 * Generate a URL-friendly slug from clinic name
 * @param {string} clinicName - The clinic name to convert
 * @returns {string} URL-safe slug
 */
export function generateSlug(clinicName) {
  if (!clinicName || typeof clinicName !== 'string') {
    return '';
  }

  return clinicName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars except spaces and hyphens
    .replace(/\s+/g, '-')         // Replace spaces with hyphens
    .replace(/-+/g, '-')          // Replace multiple hyphens with single
    .replace(/^-|-$/g, '')        // Remove leading/trailing hyphens
    .substring(0, 50);            // Limit length
}

/**
 * Validate if a slug is URL-safe and meets requirements
 * @param {string} slug - The slug to validate
 * @returns {Object} Validation result with isValid and error message
 */
export function validateSlug(slug) {
  if (!slug || typeof slug !== 'string') {
    return { isValid: false, error: 'Slug is required' };
  }

  if (slug.length < 3) {
    return { isValid: false, error: 'Slug must be at least 3 characters' };
  }

  if (slug.length > 50) {
    return { isValid: false, error: 'Slug must be 50 characters or less' };
  }

  // Check for valid characters (lowercase letters, numbers, hyphens only)
  if (!/^[a-z0-9-]+$/.test(slug)) {
    return { isValid: false, error: 'Slug can only contain lowercase letters, numbers, and hyphens' };
  }

  // Check for reserved words
  const reservedWords = ['www', 'api', 'admin', 'app', 'mail', 'ftp', 'blog', 'shop', 'store'];
  if (reservedWords.includes(slug)) {
    return { isValid: false, error: 'This slug is reserved and cannot be used' };
  }

  // Check it doesn't start or end with hyphen
  if (slug.startsWith('-') || slug.endsWith('-')) {
    return { isValid: false, error: 'Slug cannot start or end with a hyphen' };
  }

  return { isValid: true };
}

/**
 * Generate a unique slug by appending numbers if needed
 * @param {string} baseSlug - The base slug to make unique
 * @param {Function} checkAvailability - Function that returns true if slug is available
 * @returns {Promise<string>} Unique slug
 */
export async function generateUniqueSlug(baseSlug, checkAvailability) {
  let slug = baseSlug;
  let counter = 1;

  while (!(await checkAvailability(slug))) {
    slug = `${baseSlug}-${counter}`;
    counter++;
    
    // Prevent infinite loops
    if (counter > 999) {
      throw new Error('Unable to generate unique slug');
    }
  }

  return slug;
}

/**
 * Format slug for display in URLs
 * @param {string} slug - The slug to format
 * @param {string} domain - The domain (default: healehub.com)
 * @returns {string} Full URL
 */
export function formatSlugUrl(slug, domain = 'healehub.com') {
  return `${slug}.${domain}`;
}
